import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { CommonModule } from '@angular/common';

interface ErrorItem {
    code: string;
    message: string;
}

interface ResponseCodeData {
    errors: ErrorItem[];
    success: boolean;
}

@Component({
  standalone: true,
    selector: 'app-response-message-cell-renderer',
    imports: [CommonModule],
    template: `
    <div class="response-message-container" [title]="getTooltipText()">
      <!-- Show errors if we have them -->
      <div *ngIf="hasErrors()" class="error-display">
        <div class="error-header">
          <span >{{ getErrors().length }} error{{ getErrors().length > 1 ? 's' : '' }}</span>
        </div>
        <div class="error-list">
          <div *ngFor="let error of getErrors(); let i = index" class="error-item">
            <span class="error-code">{{ error.code }}:</span>
            <span class="error-message">{{ error.message }}</span>
          </div>
        </div>
      </div>
      
      <!-- Show success if we have a successful response -->
      <div *ngIf="!hasErrors() && responseData?.success" class="success-message">
        <span class="success-text">Success</span>
      </div>
      
      <!-- Show raw value if we couldn't parse it -->
      <div *ngIf="!responseData && rawValue" class="debug-info">
        <span class="debug-text">{{ rawValue }}</span>
      </div>
      
      <!-- Show nothing message if no data at all -->
      <div *ngIf="!responseData && !rawValue" class="no-data">
        <span class="no-data-text">No data</span>
      </div>
    </div>
  `,
    styles: [`
    .response-message-container {
      padding: 2px 2px;
      height: 100%;
      overflow: visible;
      font-size: 12px;
      line-height: 1.0;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    .error-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
      width: 100%;
    }

    .error-item {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 2px 0;
      border-bottom: 1px solid #f0f0f0;
    }

    .error-item:last-child {
      border-bottom: none;
    }

    .error-display {
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
      font-size: 10px;
      overflow-y: auto;
    }

    .error-header {
      font-size: 11px;
      font-weight: 600;
      color: #d32f2f;
      margin-bottom: 6px;
      background-color: #fdf2f2;
      padding: 3px 6px;
      border-radius: 3px;
      border-left: 3px solid #d32f2f;
    }

    .first-error {
      display: flex;
      flex-direction: column;
    }

    .error-code {
      font-weight: 600;
      color: #d32f2f;
      margin-bottom: 2px;
      font-size: 12px;
    }

    .error-message {
      color: #666;
      word-wrap: break-word;
      white-space: normal;
      font-size: 12px;
      line-height: 1.3;
    }

    .error-item.last-item {
      border-bottom: none;
      padding-bottom: 0;
    }

    .error-code {
      font-weight: 600;
      color: #d32f2f;
      margin-bottom: 2px;
    }

    .error-message {
      color: #666;
      word-wrap: break-word;
      white-space: normal;
    }

    .success-message {
      display: flex;
      align-items: center;
      height: 100%;
    }

    .success-text {
      color: #4caf50;
      font-weight: 500;
    }

    .debug-info {
      display: flex;
      align-items: center;
      height: 100%;
    }

    .debug-text {
      color: #ff9800;
      font-size: 11px;
      font-family: monospace;
    }

    .no-data {
      display: flex;
      align-items: center;
      height: 100%;
    }

    .no-data-text {
      color: #999;
      font-style: italic;
      font-size: 12px;
    }

    /* Ensure proper scrolling for long content */
    .response-message-container::-webkit-scrollbar {
      width: 4px;
    }

    .response-message-container::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 2px;
    }

    .response-message-container::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 2px;
    }

    .response-message-container::-webkit-scrollbar-thumb:hover {
      background: #999;
    }
  `]
})
export class ResponseMessageCellRendererComponent implements ICellRendererAngularComp {
    public responseData: ResponseCodeData | null = null;
    public rawValue: any;
    private params: ICellRendererParams | null = null;

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.rawValue = params.value;
        this.parseResponseCode(params.value);
    }

    refresh(params: ICellRendererParams): boolean {
        this.rawValue = params.value;
        this.parseResponseCode(params.value);
        return true;
    }

    private parseResponseCode(value: any): void {

        if (!value) {
            this.responseData = null;
            return;
        }

        try {
            // Handle case where value is already an object
            if (typeof value === 'object' && value.errors) {
                this.responseData = value;
                return;
            }

            // Handle case where value is a JSON string
            if (typeof value === 'string') {
                let cleanedValue = value.trim();

                // Handle the specific format you mentioned: "{"errors":[...],"success":false}\n"
                // Remove outer quotes if present
                if (cleanedValue.startsWith('"') && cleanedValue.endsWith('"')) {
                    cleanedValue = cleanedValue.slice(1, -1);
                }

                // Replace escaped quotes and newlines
                cleanedValue = cleanedValue.replace(/\\"/g, '"').replace(/\\n/g, '');

                // Try to parse as JSON
                this.responseData = JSON.parse(cleanedValue);
                return;
            }

            // Handle case where it might be a number (HTTP status code)
            if (typeof value === 'number') {
                // If it's a successful status code, treat as success
                if (value >= 200 && value < 300) {
                    this.responseData = { errors: [], success: true };
                } else {
                    this.responseData = { errors: [{ code: value.toString(), message: 'HTTP Error' }], success: false };
                }
                return;
            }

            // Handle case where it's a simple error code string (not JSON)
            if (typeof value === 'string' && !value.includes('{')) {
                this.responseData = {
                    errors: [{ code: value, message: 'Error occurred' }],
                    success: false
                };
                return;
            }

            // If we can't parse it, set to null
            this.responseData = null;
        } catch (error) {

            // If JSON parsing failed but it's a string, treat it as an error code
            if (typeof value === 'string') {
                this.responseData = {
                    errors: [{ code: value, message: 'Parse error - treating as error code' }],
                    success: false
                };
            } else {
                this.responseData = null;
            }
        }
    }

    hasErrors(): boolean {
        return !!(this.responseData?.errors && this.responseData.errors.length > 0);
    }

    getErrors(): ErrorItem[] {
        return this.responseData?.errors || [];
    }

    getTooltipText(): string {
        if (!this.responseData) {
            return this.rawValue ? String(this.rawValue) : 'No data';
        }

        if (this.hasErrors()) {
            return this.getErrors()
                .map(error => `${error.code}: ${error.message}`)
                .join('\n');
        }

        return 'Success';
    }
}
